<template>
  <component :is="componentType" class="app-button"
          :class="{
             'app-button--x-small': size === 'x-small',
             'app-button--small': size === 'small',
             'app-button--large': size === 'large',
             'app-button--medium': size === 'medium',
             'app-button--primary': type === 'primary',
             'app-button--outlined': type === 'outlined',
             'app-button--tonal': type === 'tonal',
             'app-button--ghost': type === 'ghost',
             'app-button--icon': type === 'icon',
             'app-button--full-width': fullWidth,
             'app-button--brand': variant === 'brand',
             'app-button--neutral': variant === 'neutral',
             'app-button--danger': variant === 'danger',
             'app-button--is-loading': loading,
             'app-button--custom-icon-color': isIconColorCustom,
          }"
          type="button"
          :href="href ? href : null"
          v-bind="$attrs"
          :to="to"
  >
    <span class="d-flex align-center column-gap-2" :style="{ visibility: contentVisibility }">
      <!-- @slot for left icon -->
      <div v-if="$slots['left-icon']" class="left-icon">
        <slot name="left-icon"></slot>
      </div>
      <!-- @slot for button text -->
      <slot></slot>
      <!-- @slot for right icon -->
      <div v-if="$slots['right-icon']" class="right-icon">
        <slot name="right-icon"></slot>
      </div>
    </span>
    <v-progress-circular v-show="loading" indeterminate width="3" />
  </component>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

export type AppButtonSize = 'x-small' | 'small' | 'medium' | 'large'
export type AppButtonType = 'primary' | 'outlined' | 'tonal' | 'ghost' | 'icon'
export type AppButtonVariant = 'brand' | 'neutral' | 'danger'

/**
 * @displayName Principal Button
 */
export default defineComponent({
  name: 'AppButton',
  inheritAttrs: false,
  props: {
    /**
     * Size of the button
     * Possible values are : small, medium
     */
    size: {
      type: String as PropType<AppButtonSize>,
      default: 'medium',
    },
    /**
     * Hierarchy of the button
     * Possible values are : primary, outlined, tonal, icon
     */
    type: {
      type: String as PropType<AppButtonType>,
      default: 'primary',
    },
    /**
     * Color variant
     * Possible values are : brand, neutral, danger
     */
    variant: {
      type: String as PropType<AppButtonVariant>,
      default: 'brand',
    },
    /**
     * Specifies whether the button should fill parent's width
     */
    fullWidth: {
      type: Boolean,
      default: false,
    },
    /**
     * If true, replace button text with a loader and disable pointer events
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Link object
     */
    to: {
      type: Object as PropType<object | null>,
      default: null,
    },
    href: {
      type: String,
      default: '',
    },
    isIconColorCustom: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    contentVisibility () {
      return this.loading ? 'hidden' : 'visible'
    },
    componentType (): string {
      if (this.to) {
        return 'router-link'
      } else if (this.href) {
        return 'a'
      }
      return 'button'
    },
  },
})
</script>

<style scoped lang="scss">
/**
  Note: this is likely what we want everywhere else, since a button is usually inline
  But this is an additive change instead of changing everything else everywhere
*/
a.app-button {
  display: inline-flex;
}

.app-button {
  border-radius: 4px;
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;

  &:disabled {
    cursor: default;
  }

  &.app-button--is-loading {
    pointer-events: none;
  }

  & .v-progress-circular {
    height: 22px !important;
    width: 22px !important;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  &.app-button--full-width {
    width: 100%;
  }

  &.app-button--small {
    min-height: 28px;
    padding: 5px 8px;
    font-size: 14px;
    line-height: 14px;
    font-weight: 500;
    column-gap: 4px;

    & .v-progress-circular {
      height: 12px !important;
      width: 12px !important;
    }
  }

  &.app-button--medium {
    min-height: 40px;
    padding: 8px 16px;
    font-size: 14px;
    line-height: normal;
    font-weight: 500;
    column-gap: 8px;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
  }

  // all buttons EXCEPT primary brand
  &:not(.app-button--primary.app-button--brand):hover:enabled::after {
    background-color: rgba(var(--v-theme-surface-hover));
  }

  &:not(.app-button--primary.app-button--brand):active:enabled::after {
    background-color: rgba(var(--v-theme-surface-pressed));
  }

  &.app-button--primary {
    &.app-button--brand {
      background-color: rgb(var(--v-theme-surface-brand-bold));
      color: rgb(var(--v-theme-white));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-surface-brand-bold-hover));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-surface-brand-bold-pressed));
      }
    }
    &.app-button--danger {
      color: rgb(var(--v-theme-white));
      outline-color: rgb(var(--v-theme-error));
      background-color: rgb(var(--v-theme-error));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-error-darken-1));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-error-darken-2));
      }
    }
    &:disabled {
      background-color: rgb(var(--v-theme-surface-disabled));
      color: rgb(var(--v-theme-text-disabled));
    }
  }

  &.app-button--outlined {
    background-color: rgb(var(--v-theme-white));
    color: rgb(var(--v-theme-primary));
    outline: 1px solid transparent;
    outline-offset: -1px;

    &.app-button--brand {
      color: rgb(var(--v-theme-primary));
      outline-color: rgb(var(--v-theme-primary));
    }
    &.app-button--neutral {
      color: rgb(var(--v-theme-text-tertiary));
      outline-color: rgb(var(--v-theme-border-secondary));
    }
    &.app-button--danger {
      color: rgb(var(--v-theme-error));
      outline-color: rgb(var(--v-theme-error));
    }

    &:disabled {
      color: rgb(var(--v-theme-grey-300));
      outline-color: rgb(var(--v-theme-grey-100));
    }
  }

  &.app-button--tonal {
    border: 1px solid transparent;
    &.app-button--brand {
      color: rgb(var(--v-theme-primary));
      background-color: rgb(var(--v-theme-primary-lighten-5));
      &:hover:enabled {
        background-color: rgb(var(--v-theme-primary-lighten-4));
      }
      &:active:enabled {
        color: rgb(var(--v-theme-primary-lighten-2));
        background-color: rgb(var(--v-theme-primary-lighten-4));
      }
    }
    &.app-button--neutral {
      color: rgb(var(--v-theme-grey-700));
      background-color: rgb(var(--v-theme-grey-50));
      border-color: rgb(var(--v-theme-grey-300));
    }

    &:disabled {
      color: rgb(var(--v-theme-grey-300));
      background-color: rgb(var(--v-theme-grey-50));
    }
  }

  &.app-button--ghost {
    background-color: transparent;
    color: rgb(var(--v-theme-grey-700)) !important;
    border-radius: 4px;

    &:hover:enabled {
      background-color: rgb(var(--v-theme-grey-50));
    }

    &.--is-active {
      background-color: rgb(var(--v-theme-grey-100)) !important;
    }

    &:active:enabled {
      background-color: rgb(var(--v-theme-grey-50));
      color: rgb(var(--v-theme-grey-400));
    }

    &.app-button--danger {
      color: rgb(var(--v-theme-error));
      outline-color: rgb(var(--v-theme-error));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-error-lighten-1));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-white));
        color: rgb(var(--v-theme-error))
      }
    }

    &:disabled {
      color: rgb(var(--v-theme-grey-300));
    }
  }

  &.app-button--icon {
    background-color: transparent;
    color: rgb(var(--v-theme-icon-primary));
    height: 36px;
    min-height: 36px;
    min-width: 36px;
    padding: 4px;

    &.app-button--x-small {
      height: 20px;
      min-height: 20px;
      min-width: 20px;
      border-radius: 999px;
    }

    &.app-button--small {
      height: 28px;
      min-height: 28px;
      min-width: 28px;
    }

    &.app-button--large {
      height: 40px;
      min-height: 40px;
      min-width: 40px;
    }

    &.--is-active::after {
      background-color: rgba(var(--v-theme-surface-pressed)) !important;
    }

    &:disabled {
      color: rgb(var(--v-theme-icon-disabled));
    }

    &.border {
      border: 1px solid rgb(var(--v-theme-grey-300)) !important;
    }
  }

  &.app-button--counter {
    height: 28px;
    padding: 4px 8px;

    &.app-button--small {
      padding: 5px;
    }

    &.app-button--medium {
      padding: 5px;
    }

    span {
      column-gap: 4px;
    }
  }

  .left-icon,
  .right-icon {
    display: flex;
  }

  &.app-button--custom-icon-color:disabled {
    .left-icon, .right-icon {
      :deep(span) {
        color: rgb(var(--v-theme-icon-disabled)) !important;
      }
    }
  }

  &:not(.app-button--custom-icon-color) {
    .left-icon,
    .right-icon {
      :deep(span) {
        color: currentColor !important;
      }
    }
  }

  &.app-button--compact {
    span {
      column-gap: 4px;
    }
    :deep(svg) {
      padding: 2px;
    }
  }
}
</style>
